import type { ReactNode } from "react";

import type { RegistroDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { ImagenDeTerreno, UbicacionDeLaFotoDeTerreno } from "./FotosDeTerreno";
import {
  atributoDeResaltado,
  CLASE_DE_FOTO_RESALTADA,
  CLASE_DE_TARJETA_RESALTADA,
} from "./MarcasDeSincronizacion";
import {
  Rotulo,
  TarjetaDeAudio,
  useResaltadoDeLaCaptura,
} from "./PanelDeTerrenoDeLaDescripcion";
import { fotosDelRegistro, horaDeCaptura, numeroDeRegistro, tiposDelRegistro } from "./registroDeTerreno";

/** Una foto del registro con su propio resaltado: cada una puede venir de otra sincronización. */
function FotoDelRegistro({
  sincronizacionId,
  children,
}: {
  sincronizacionId: string | null | undefined;
  children: (resaltado: ReturnType<typeof useResaltadoDeLaCaptura>) => React.ReactNode;
}) {
  const resaltado = useResaltadoDeLaCaptura(sincronizacionId);

  return <div className="flex flex-col gap-2">{children(resaltado)}</div>;
}

interface TarjetaDeRegistroDeTerrenoProps {
  informeId: number;
  registro: RegistroDeTerreno;
  /** La posición entre TODOS los registros del ítem: numera "Registro 01". */
  indice: number;
  /** De qué es la fotografía, para su texto alternativo ("Fotografía · Tranque · Registro 01"). */
  de: string;
  /** Abre la foto en el visor de la pestaña. */
  onAbrirFoto: (uuid: string) => void;
}

/**
 * Un registro de terreno de UN ítem —su foto, sus notas de voz con la
 * transcripción y su texto—, tal como lo ven los paneles de construcciones y
 * de aguas.
 *
 * Es de LECTURA: no copia nada a ningún formulario. Lo único que ofrece es
 * ampliar la foto en el visor de la pestaña.
 */
export function TarjetaDeRegistroDeTerreno({
  informeId,
  registro,
  indice,
  de,
  onAbrirFoto,
}: TarjetaDeRegistroDeTerrenoProps) {
  const r = registro;
  const deTexto = useResaltadoDeLaCaptura(r.sincronizacion_id);
  const fotos = fotosDelRegistro(r).filter((f) => f.archivo !== null);

  return (
    <div className="border-border bg-card flex flex-col gap-2 rounded-xl border px-2.5 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-ink text-xs font-extrabold">{numeroDeRegistro(indice)}</span>
        <span className="text-ink-hint font-mono text-[0.65625rem] font-semibold">
          {horaDeCaptura(r.capturado_en)}
        </span>
        <span className="text-ink-hint ml-auto text-[0.65625rem] font-semibold">
          {tiposDelRegistro(r)}
        </span>
        <BotonDescartarDeTerreno uuid={r.uuid} etiqueta={`registro · ${de} · ${numeroDeRegistro(indice)}`} />
      </div>

      {fotos.map((foto, i) => {
        const sufijo = fotos.length > 1 ? ` · Foto ${i + 1} de ${fotos.length}` : "";

        return (
          <FotoDelRegistro key={foto.uuid} sincronizacionId={foto.sincronizacion_id}>
            {(resaltado) => (
            <>
            <button
              {...resaltado.atributo}
              type="button"
              onClick={() => onAbrirFoto(foto.uuid)}
              aria-label={`Ampliar fotografía · ${numeroDeRegistro(indice)}${sufijo}`}
              title="Ampliar"
              className={cn(
                "bg-muted hover:ring-primary focus-visible:ring-ring/50 h-[9.375rem] overflow-hidden rounded-lg outline-none hover:ring-2 focus-visible:ring-[3px]",
                resaltado.on && CLASE_DE_FOTO_RESALTADA,
              )}
            >
              <ImagenDeTerreno
                informeId={informeId}
                uuid={foto.uuid}
                hash={foto.archivo!.hash}
                mime={foto.archivo!.mime}
                alt={`Fotografía · ${de} · ${numeroDeRegistro(indice)}${sufijo}`}
              />
            </button>
            <UbicacionDeLaFotoDeTerreno latitud={foto.latitud} longitud={foto.longitud} />
            </>
            )}
          </FotoDelRegistro>
        );
      })}

      {r.audios.map((audio) => (
        <TarjetaDeAudio key={audio.uuid} informeId={informeId} audio={audio} />
      ))}

      {r.texto ? (
        <div
          {...atributoDeResaltado(deTexto.on)}
          className={cn(
            "rounded-lg border px-2.5 py-2",
            deTexto.on ? CLASE_DE_TARJETA_RESALTADA : "border-border bg-superficie-sutil",
          )}
        >
          <div className="flex items-center gap-1.5">
            <Rotulo>Texto</Rotulo>
            {deTexto.tag}
          </div>
          <p className="text-ink-body mt-0.5 text-xs leading-normal font-medium text-pretty">
            {r.texto}
          </p>
        </div>
      ) : null}
    </div>
  );
}
